import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import classes from "./ReservationsByChannelPie.module.scss";
import { Legend } from "../../../../common/ChartLegend/ChartLegend";
import { Divider } from "@mui/material";
import { Toast } from "../../../../common/Toast/Toast";
import { isMobileModeSelector } from "../../../../../redux/selectors/appStatusSelector";
import { performancePagePeriodSelector } from "../../../../../redux/selectors/performancePageSelector";
import { Typography } from "../../../../../shared/ui/typography";
import React from "react";
import { PieLabelRenderProps } from "recharts";
import clsx from "clsx";
import { getReservationsDistributionsByChannel } from "@/redux/selectors/reservationsData";
import { getPeriodAbbreviation } from "@/utils/getPeriodAbbreviation";
import { useSelector } from "react-redux";

const getOccupancyPieLegend = (isMobileView: boolean) => [
  {
    label: "Airbnb",
    color: "#FF5A5F",
    legendType: "circle",
  },
  {
    label: isMobileView ? "Booking" : "Booking.com",
    color: "#003580",
    legendType: "circle",
  },
  {
    label: "Vrbo",
    color: "#002B49",
    legendType: "circle",
  },
  {
    label: "Others",
    color: "#FECF0A",
    legendType: "circle",
  },
  {
    label: "Direct Booking",
    color: "#12B76A",
    legendType: "circle",
  },
];

const renderCustomLabel = (props: PieLabelRenderProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, value } = props;
  const RADIAN = Math.PI / 180;

  const radius = Number(outerRadius) + 20;
  const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
  const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);

  const insideRadius =
    Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) / 2;
  const insideX = Number(cx) + insideRadius * Math.cos(-midAngle * RADIAN);
  const insideY = Number(cy) + insideRadius * Math.sin(-midAngle * RADIAN);

  const edgeRadius = Number(outerRadius);
  const edgeX = Number(cx) + edgeRadius * Math.cos(-midAngle * RADIAN);
  const edgeY = Number(cy) + edgeRadius * Math.sin(-midAngle * RADIAN);

  if (value < 5) {
    return (
      <>
        <line
          x1={edgeX}
          y1={edgeY}
          x2={x}
          y2={y}
          stroke="#000000"
          strokeWidth={1}
        />
        <text
          x={x}
          y={y}
          offset={8}
          fill="#000000"
          textAnchor={Number(x) > Number(cx) ? "start" : "end"}
          dominantBaseline="central"
        >
          {value.toFixed(0)}%
        </text>
      </>
    );
  }

  return (
    <text
      x={insideX}
      y={insideY}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {value.toFixed(0)}%
    </text>
  );
};

const DATA_TO_DISPLAY = [
  { name: "Airbnb", id: "airbnb", color: "#FF5A5F" },
  { name: "Booking.com", id: "booking", color: "#003580" },
  { name: "VRBO", id: "vrbo", color: "#002B49" },
  { name: "Others", id: "others", color: "#FECF0A" },
  {
    name: "Direct Booking",
    id: "direct",
    color: "#12B76A",
  },
];

const calculatePercentagesForData = (data: { [key: string]: number }) => {
  const total = Object.values(data).reduce(
    (acc: number, item: number) => acc + item,
    0
  );

  return Object.entries(data).map(([key, value]) => {
    const dataItem = DATA_TO_DISPLAY.find((item) => item.id === key);

    return {
      name: dataItem?.name || "",
      color: dataItem?.color || "",
      value: Number(((value / total) * 100).toFixed(2)),
    };
  });
};

export const ReservationsByChannelPie = () => {
  const period = useSelector(performancePagePeriodSelector);
  const isMobileView = useSelector(isMobileModeSelector);

  const reservationData = useSelector(getReservationsDistributionsByChannel);
  const periodAbbreviation = getPeriodAbbreviation(period);

  const reservationDistributionByPeriod =
    reservationData && reservationData[periodAbbreviation];

  const formattedData = calculatePercentagesForData(
    reservationDistributionByPeriod || {}
  );

  if (
    !reservationData ||
    Object.values(reservationDistributionByPeriod ?? {}).every(
      (value) => value === 0
    )
  ) {
    return (
      <div className={clsx(classes.container, classes.errorMessageContainer)}>
        <Typography
          variant="text-lg"
          weight="medium"
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.revenuePieContainer}>
      <div className={classes.revenuePieHeader}>
        <Typography
          variant={isMobileView ? "text-md" : "text-xl"}
          weight="semibold"
          className={classes.gray900}
        >
          Revenue Distribution by Channel
        </Typography>
      </div>
      {!isMobileView ? <Divider /> : null}
      <div className={classes.pieContainer}>
        {formattedData ? (
          <>
            <ResponsiveContainer
              width={isMobileView ? "100%" : "60%"}
              height={isMobileView ? 270 : "90%"}
            >
              <PieChart width={206} height={206}>
                <Pie
                  cx={"50%"}
                  cy={"50%"}
                  data={formattedData.filter((entry) => entry.value > 0)}
                  dataKey="value"
                  fill="#8884d8"
                  isAnimationActive={false}
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={isMobileView ? 103 : 95}
                  legendType="circle"
                  paddingAngle={1}
                  minAngle={1}
                >
                  {formattedData.map((entry, index) => {
                    if (entry.value === 0) {
                      return null;
                    }
                    return (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        stroke="none"
                      />
                    );
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {isMobileView ? (
              <div className={classes.legendBlock}>
                <Legend
                  className={`${classes.legend} ${classes.legendRow}`}
                  legendData={getOccupancyPieLegend(isMobileView).slice(0, 2)}
                />
                <Legend
                  className={`${classes.legend} ${classes.legendRow}`}
                  legendData={getOccupancyPieLegend(isMobileView).slice(2, 4)}
                />
              </div>
            ) : (
              <Legend
                className={classes.legend}
                legendData={getOccupancyPieLegend(isMobileView)}
              />
            )}
          </>
        ) : (
          <Toast type="info" />
        )}
      </div>
    </div>
  );
};
